import { useQueryRulesAsPublic } from "@gymflow/api";
import { canRescheduleAppointment, rulesHelper } from "@gymflow/helpers";
import { AppointmentDTO } from "@gymflow/types";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { Button, EditIcon } from "../../../atoms";
import { CancelIcon } from "../../../atoms/icons/CancelIcon";
import { HostedPagesProfileBookingAppointmentsCancelModal } from "./HostedPagesProfileBookingAppointmentsCancelModal";

type HostedPagesProfileBookingAppointmentsOverviewActionsProps = {
  appointmentId: string;
  dataAppointment: AppointmentDTO | undefined;
};

export const HostedPagesProfileBookingAppointmentsOverviewActions = ({
  appointmentId,
  dataAppointment,
}: HostedPagesProfileBookingAppointmentsOverviewActionsProps) => {
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const history = useHistory();
  const { api } = useGymflowModels();
  const { t } = useTranslation();
  const { setModal, hide } = useContext(ModalContext);
  const { club } = useClubSettings();

  const { data: appointmentRules } = useQueryRulesAsPublic({
    api,
    params: dataAppointment
      ? { appointableId: dataAppointment?.appointable.id }
      : undefined,
  });

  const { rescheduleNoticeRule } =
    rulesHelper.mapRuleGroupToAppointmentRules(appointmentRules);
  const canReschedule = useMemo(
    () =>
      canRescheduleAppointment(
        dataAppointment,
        rescheduleNoticeRule,
        club.timezone,
      ),
    [dataAppointment, rescheduleNoticeRule, club.timezone],
  );

  return (
    <>
      <div className="mt-3.5 h-px w-full bg-gray-300 dark:bg-gray-700" />
      <div className="flex flex-col">
        {canReschedule && (
          <Button
            intent="transparent"
            className="justify-start gap-2 bg-transparent pl-0 hover:opacity-50"
            onClick={() => {
              history.push(
                createSiteOrEmbedLink(
                  RouteFeature.AppointableIdSelectReschedule.replace(
                    ":appointmentId",
                    appointmentId,
                  ),
                ),
              );
            }}
          >
            <EditIcon
              className="size-5"
              pathClassName="stroke-gray-500 dark:stroke-gray-400"
            />
            <div className="dark:text-gray-25 text-sm font-medium text-gray-950">
              {t(
                "components.hostedPagesProfileBookingAppointmentsOverview.menuOptions.reschedule",
              )}
            </div>
          </Button>
        )}
        <Button
          intent="transparent"
          className="justify-start gap-2 bg-transparent pl-0 hover:opacity-50"
          onClick={() => {
            if (!dataAppointment) {
              return;
            }
            setModal(
              <HostedPagesProfileBookingAppointmentsCancelModal
                appointmentInfo={dataAppointment}
                onCancel={() => hide()}
              />,
            );
          }}
        >
          <CancelIcon
            className="size-5"
            pathClassName="stroke-error-600 dark:stroke-error-200"
          />
          <div className="dark:text-error-200 text-error-600 text-sm font-medium">
            {t(
              "components.hostedPagesProfileBookingAppointmentsOverview.menuOptions.cancelAppointment",
            )}
          </div>
        </Button>
      </div>
    </>
  );
};
